import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const Select = ({ children, value, onValueChange, className = '', ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newValue) => {
    if (onValueChange) {
      onValueChange(newValue);
    }
    setIsOpen(false);
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (!child) return null;

    if (child.type === SelectTrigger) {
      return React.cloneElement(child, {
        onClick: () => setIsOpen(!isOpen),
        isOpen,
        value,
      });
    }

    if (child.type === SelectContent) {
      return React.cloneElement(child, {
        isOpen,
        value,
        onSelect: handleSelect,
      });
    }

    return child;
  });

  return (
    <div className={`relative inline-block w-full ${className}`} ref={selectRef} {...props}>
      {childrenWithProps}
    </div>
  );
};

const SelectTrigger = ({ children, onClick, isOpen, value, className = '', ...props }) => {
  const childrenWithProps = React.Children.map(children, (child) => {
    if (!child) return null;
    if (child.type === SelectValue) {
      return React.cloneElement(child, { value });
    }
    return child;
  });

  return (
    <button
      type="button"
      className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      onClick={onClick}
      {...props}
    >
      {childrenWithProps}
      <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </button>
  );
};

const SelectValue = ({ placeholder, value }) => {
  return <span>{value || placeholder}</span>;
};

const SelectContent = ({ children, isOpen, onSelect, value, className = '' }) => {
  if (!isOpen) return null;

  const childrenWithProps = React.Children.map(children, (child) => {
    if (!child) return null;
    if (child.type === SelectItem) {
      return React.cloneElement(child, {
        onSelect: onSelect,
        isSelected: child.props.value === value,
      });
    }
    return child;
  });

  return (
    <div className={`absolute top-[calc(100%+4px)] left-0 right-0 z-50 max-h-60 overflow-auto rounded-md border bg-white shadow-lg ring-1 ring-black/5 p-1 ${className}`}>
      {childrenWithProps}
    </div>
  );
};

const SelectItem = ({ children, value, onSelect, isSelected, className = '', ...props }) => {
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof onSelect === 'function') {
      onSelect(value);
    }
  };

  return (
    <div
      className={`relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-3 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
        } ${className}`}
      onClick={handleClick}
      role="option"
      aria-selected={isSelected}
      {...props}
    >
      {children}
    </div>
  );
};

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
